import {
  Button,
  EmptyState,
  Input,
  LoadingState,
  TabButton,
} from '@components/common';
import {
  FolderKanban,
  LayoutGrid,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import { fetchAccessControls } from '../../../services/accessControlService';
import {
  createTourElementResolver,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import {
  AccessControlArea,
  AccessControlRecord,
} from '../../../types/accessControl';

const AREA_LABELS: Record<AccessControlArea, string> = {
  [AccessControlArea.DASHBOARD]: 'Dashboard',
  [AccessControlArea.SALES]: 'Sales',
  [AccessControlArea.ACCOMMODATION]: 'Accommodation',
  [AccessControlArea.ACCOUNTING]: 'Accounting',
  [AccessControlArea.INVENTORY]: 'Inventory',
  [AccessControlArea.SETTINGS]: 'Settings',
};

export const AccessControlsPage: React.FC = () => {
  const [controls, setControls] = useState<AccessControlRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeArea, setActiveArea] = useState<AccessControlArea>(
    AccessControlArea.DASHBOARD,
  );

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: createTourElementResolver([
          onboardingTourSelector(
            ONBOARDING_TOUR_TARGETS.securityAccessControlsLink,
          ),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.accessControlsPage),
        ]),
        popover: {
          title: 'Start from Access Controls',
          description:
            'Review the global control catalog first, then move to Policies and Role Assignments to apply those controls to your outlet roles.',
          side: 'bottom' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.accessControlsSearch,
        ),
        popover: {
          title: 'Search the control catalog',
          description:
            'Use search to quickly find the permissions you need before building policies and assigning them to roles.',
          side: 'bottom' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.accessControlsPage,
        ),
        popover: {
          title: 'Review the available controls',
          description:
            'The dashboard and sales controls listed here are the building blocks you will use for company policies and role assignments.',
          side: 'top' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    stepId: 'security',
    steps: onboardingTourSteps,
  });

  const loadControls = async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    const response = await fetchAccessControls();

    if (!response.success) {
      setError(response.error || 'Failed to load access controls');
      setControls([]);
    } else {
      setControls(response.data || []);
      setError(null);
    }

    setIsLoading(false);
    setIsRefreshing(false);
  };

  useEffect(() => {
    loadControls();
  }, []);

  const filteredControls = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return controls.filter((control) => {
      if (!normalizedSearch) {
        return true;
      }

      return [control.name, control.code, control.description]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [controls, searchQuery]);

  const groupedControls: Record<AccessControlArea, AccessControlRecord[]> =
    useMemo(
      () =>
        filteredControls.reduce<
          Record<AccessControlArea, AccessControlRecord[]>
        >(
          (accumulator, control) => {
            accumulator[control.area] = accumulator[control.area] || [];
            accumulator[control.area].push(control);
            return accumulator;
          },
          {
            [AccessControlArea.DASHBOARD]: [],
            [AccessControlArea.SALES]: [],
            [AccessControlArea.ACCOMMODATION]: [],
            [AccessControlArea.ACCOUNTING]: [],
            [AccessControlArea.INVENTORY]: [],
            [AccessControlArea.SETTINGS]: [],
          },
        ),
      [filteredControls],
    );

  // One tab per area that has (search-matching) controls, in AREA_LABELS order.
  const areaTabs = useMemo(
    () =>
      (Object.keys(groupedControls) as AccessControlArea[]).filter(
        (area) => groupedControls[area].length > 0,
      ),
    [groupedControls],
  );

  // Keep a tab with results selected as the search narrows the catalog.
  useEffect(() => {
    if (areaTabs.length && !areaTabs.includes(activeArea)) {
      setActiveArea(areaTabs[0]);
    }
  }, [activeArea, areaTabs]);

  const activeControls = groupedControls[activeArea] ?? [];

  if (isLoading) {
    return <LoadingState message="Loading access controls..." />;
  }

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.accessControlsPage}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Total controls</p>
            <ShieldCheck size={18} className="text-pos-primary" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {controls.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Dashboard</p>
            <LayoutGrid size={18} className="text-pos-primary" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {groupedControls[AccessControlArea.DASHBOARD].length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Sales</p>
            <FolderKanban size={18} className="text-pos-primary" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {groupedControls[AccessControlArea.SALES].length}
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Global access controls
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              These controls are shared across all companies and become the
              building blocks for each company policy.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-80">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <Input
                value={searchQuery}
                onChange={(value) => setSearchQuery(String(value))}
                placeholder="Search controls"
                className="pl-10"
                data-onboarding-tour={
                  ONBOARDING_TOUR_TARGETS.accessControlsSearch
                }
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadControls(true)}
              isLoading={isRefreshing}
            >
              <RefreshCw size={16} />
              Refresh
            </Button>
          </div>
        </div>

        {!filteredControls.length ? (
          <div className="border-t border-gray-100 p-4">
            <EmptyState
              title="No access controls found"
              description="Try a different search term to find the control you need."
            />
          </div>
        ) : (
          <>
            <div className="border-b border-gray-200 px-4">
              <nav
                className="flex gap-6 overflow-x-auto"
                aria-label="Access control areas"
              >
                {areaTabs.map((area) => (
                  <TabButton
                    key={area}
                    label={`${AREA_LABELS[area]} (${groupedControls[area].length})`}
                    isActive={activeArea === area}
                    onClick={() => setActiveArea(area)}
                  />
                ))}
              </nav>
            </div>

            <div className="bg-app-bg/40 px-5 py-4">
              <h3 className="text-base font-semibold text-black">
                {AREA_LABELS[activeArea]} controls
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {activeControls.length} control
                {activeControls.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="max-h-[32rem] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white text-left text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500 shadow-[0_1px_0_0_rgb(243,244,246)]">
                  <tr>
                    <th className="px-5 py-3">Control</th>
                    <th className="px-5 py-3">Code</th>
                    <th className="px-5 py-3">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {activeControls.map((control) => (
                    <tr key={control.id} className="border-t border-gray-100">
                      <td className="px-5 py-4 font-semibold text-black">
                        {control.name}
                      </td>
                      <td className="px-5 py-4">
                        <span className="rounded-full bg-app-bg px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700">
                          {control.code}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {control.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
